import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { Amplify } from 'aws-amplify';

import App from './App';
import { store } from './store/store';
import DynamicThemeProvider from './theme/DynamicThemeProvider';
import reportWebVitals from './reportWebVitals';
import awsExports from './aws-exports';
import { initGraphqlAuthMode, primeGraphqlAuthMode } from './utils/graphqlAuthMode';

// Inter is served from the bundle, not fonts.googleapis.com. The Google
// stylesheet was render-blocking and duplicated a font we already ship. These
// live here rather than in HomePage because every page uses them: 700 and 600
// alone account for ~140 of the fontWeight declarations in the app.
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';

// Fraunces is the display face: a soft serif with real optical wonk, which
// suits a permaculture farm in handloom country better than a geometric sans.
// Body copy stays Inter -- the pairing is the point.
import '@fontsource/fraunces/300.css';
import '@fontsource/fraunces/400.css';
import '@fontsource/fraunces/500.css';
import '@fontsource/fraunces/600.css';
import '@fontsource/fraunces/700.css';

// Configure Amplify
try {
  Amplify.configure(awsExports);
  
  // Verify configuration
  const currentConfig = Amplify.getConfig();
  console.log('✅ Amplify configured successfully');
  console.log('📋 GraphQL Endpoint:', currentConfig.API?.GraphQL?.endpoint);
  console.log('📋 S3 Bucket:', currentConfig.Storage?.S3?.bucket);
} catch (error) {
  console.error('❌ Failed to configure Amplify:', error);
  
  // Fallback to environment variables (for Amplify hosting)
  if (process.env.REACT_APP_AWS_REGION) {
    const config = {
      aws_project_region: process.env.REACT_APP_AWS_REGION,
      aws_appsync_graphqlEndpoint: process.env.REACT_APP_GRAPHQL_ENDPOINT,
      aws_appsync_region: process.env.REACT_APP_AWS_REGION,
      aws_appsync_authenticationType: "AWS_IAM",
      aws_cognito_region: process.env.REACT_APP_AWS_REGION,
      aws_user_pools_id: process.env.REACT_APP_USER_POOL_ID,
      aws_user_pools_web_client_id: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID,
      aws_cognito_identity_pool_id: process.env.REACT_APP_IDENTITY_POOL_ID,
      aws_cognito_username_attributes: ["EMAIL"],
      aws_cognito_social_providers: [],
      aws_cognito_signup_attributes: ["EMAIL"],
      aws_cognito_mfa_configuration: "OFF",
      aws_cognito_mfa_types: ["SMS"],
      aws_cognito_password_protection_settings: {
        passwordPolicyMinLength: 8,
        passwordPolicyCharacters: []
      },
      aws_cognito_verification_mechanisms: ["EMAIL"],
      aws_user_files_s3_bucket: process.env.REACT_APP_S3_BUCKET,
      aws_user_files_s3_bucket_region: process.env.REACT_APP_AWS_REGION,
      oauth: {}
    };
    console.log('🔧 Configuring Amplify with environment variables');
    Amplify.configure(config);
  } else {
    console.log('🔧 No AWS configuration found - app will run without backend');
  }
}

// Guests read the public catalog over IAM; signed-in users switch to the
// user-pool token so owner/group rules apply. See utils/graphqlAuthMode.
// Synchronous first, so the very first query already carries the right
// credential; the async pass then confirms it against the real session.
primeGraphqlAuthMode();
initGraphqlAuthMode();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <BrowserRouter>
        <DynamicThemeProvider>
          <CssBaseline />
          <App />
        </DynamicThemeProvider>
      </BrowserRouter>
    </Provider>
  </React.StrictMode>
);

// Performance measuring
reportWebVitals();